import React, {useCallback, useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import axios from "axios";
import {Spinner} from "components/Spinner";
import {hideNotice, showNotice} from "redux/actions/setAppActions";
import {BackButton} from "components/BackButton";
import {Layout} from "components/Layout";
import {Notice} from "components/Notice";
import {SureAlert} from "components/SureAlert";

/**
 * @api {get} /classes/:id/pupils/transfer страница переноса учеников класса
 * @apiGroup schools and classes
 * @apiName страница переноса учеников класса
 *
 * @apiParam {String} id ID класса
 *
 * @apiDescription <p>Страница переноса учеников класса</p>
 * <p>Компонент <code>PupilsTransfer.js</code> <a href="../client/src/pages/PupilsTransfer.js">ссылка на файл</a></p>
 */

// Внутренний компонент модалки переноса учеников
const TransferModal = ({
                           isOpen, onClose, selectedPupilIds = [], pupils = [], onTransferred = () => {
    }
                       }) => {
    const {t} = useTranslation("pupils");
    const [step, setStep] = useState(1); // 1: школы, 2: классы, 3: подтверждение
    const [isLoading, setIsLoading] = useState(false);
    const [schools, setSchools] = useState([]);
    const [classes, setClasses] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    const [hoveredConfirm, setHoveredConfirm] = useState(false);
    const [hoveredCancel, setHoveredCancel] = useState(false);
    const {id} = useParams()
    // Централизованный сброс локального состояния модалки
    const resetState = () => {
        setStep(1);
        setIsLoading(false);
        setSchools([]);
        setClasses([]);
        setSelectedSchool(null);
        setSelectedClass(null);
        setIsSuccess(false);
    };

    // Закрытие модалки с очисткой
    const handleClose = () => {
        resetState();
        onClose?.();
    };

    // Закрытие по ESC
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") handleClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // При открытии модалки загружаем список школ
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                setIsLoading(true);
                const {data} = await axios.get(`schools`);
                setSchools(data || []);
            } catch (e) {
                // Можно добавить уведомление в будущем
                console.error("Failed to load schools", e);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [isOpen]);

    // Выбор школы -> шаг 2 и загрузка классов
    const selectSchool = async (school) => {
        setSelectedSchool(school);
        setStep(2);
        setClasses([]);
        setSelectedClass(null);
        try {
            setIsLoading(true);
            const {data} = await axios.get(`schools/${school._id || school.id}/classes`);
            setClasses(data || []);
        } catch (e) {
            console.error("Failed to load classes", e);
        } finally {
            setIsLoading(false);
        }
    };

    // Выбор класса -> шаг 3
    const selectClassHandler = (cls) => {
        setSelectedClass(cls);
        setStep(3);
    };

    // Подтверждение переноса
    const handleConfirm = async () => {
        const selectedPupilsFull = pupils
            .filter((p) => selectedPupilIds.includes(p._id))
            .map(p => p._id)

        const payload = {
            classId: selectedClass._id,
            pupilsId: selectedPupilsFull,
        };
        try {
            await axios.put(`pupil/transfer`, payload);
            // Сообщаем родителю какие ученики перенесены
            onTransferred(selectedPupilsFull, selectedClass._id);
        } catch (e) {
            console.log(e)
        }
        setIsSuccess(true);
        setTimeout(() => {
            handleClose();
        }, 2000);
    };

    if (!isOpen) return null;

    // Стили модалки
    const overlayStyle = {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
    };
    const modalStyle = {
        background: "#fff",
        borderRadius: 12,
        width: "100%",
        maxWidth: 600,
        maxHeight: "90vh",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
        display: "flex",
        flexDirection: "column",
    };
    const headerStyle = {
        padding: "16px 20px",
        borderBottom: "1px solid #eee",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    };
    const bodyStyle = {
        padding: 20,
        overflow: "auto",
    };
    const footerStyle = {
        padding: 16,
        borderTop: "1px solid #eee",
        display: "flex",
        gap: 12,
        justifyContent: "flex-end",
    };
    const confirmBtnStyle = (hovered, disabled) => ({
        border: "1px solid",
        borderColor: disabled ? "#e5e7eb" : hovered ? "#a5b4fc" : "#c7d2fe",
        background: disabled ? "#f3f4f6" : hovered ? "#e0e7ff" : "#eef2ff",
        color: disabled ? "#9ca3af" : "#3730a3",
        borderRadius: 10,
        padding: "10px 16px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all .15s ease",
        boxShadow: hovered && !disabled ? "0 6px 16px rgba(76,111,255,0.18)" : "none",
        transform: hovered && !disabled ? "translateY(-1px)" : "none",
    });
    const cancelBtnStyle = (hovered) => ({
        border: "1px solid",
        borderColor: hovered ? "#d1d5db" : "#e5e7eb",
        background: hovered ? "#f3f4f6" : "#f8fafc",
        color: "#374151",
        borderRadius: 10,
        padding: "10px 16px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "all .15s ease",
        boxShadow: hovered ? "0 4px 12px rgba(17,24,39,0.08)" : "none",
        transform: hovered ? "translateY(-1px)" : "none",
    });
    const listStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 12,
    };
    const itemStyle = (active, hovered) => ({
        border: active || hovered ? "1px solid #4c6fff" : "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
        cursor: "pointer",
        background: active ? "#f0f4ff04" : hovered ? "#f9fbff04" : "#fff",
        transition: "all .15s ease",
        boxShadow: hovered ? "0 4px 12px rgba(76,111,255,0.15)" : "none",
        transform: hovered ? "translateY(-1px)" : "none",
    });
    const closeBtnStyle = {
        border: "none",
        background: "transparent",
        fontSize: 20,
        cursor: "pointer",
        lineHeight: 1,
    };

    const stop = (e) => e.stopPropagation();

    const selectedPupilsFull = pupils.filter((p) => selectedPupilIds.includes(p._id));

    return (
        <div style={overlayStyle} onClick={handleClose}>
            <div style={modalStyle} onClick={stop}>
                <div style={headerStyle}>
                    <strong>
                        {step === 1 && t("choose_school", {defaultValue: "Выберите школу"})}
                        {step === 2 && t("choose_class", {defaultValue: "Выберите класс"})}
                        {step === 3 && t("confirm_move", {defaultValue: "Подтверждение переноса"})}
                    </strong>
                    <button aria-label="close" style={closeBtnStyle} onClick={handleClose}>
                        ×
                    </button>
                </div>

                <div style={bodyStyle}>
                    {isLoading ? (
                        <div style={{display: "flex", alignItems: "center", justifyContent: "center", minHeight: 220}}>
                            <Spinner/>
                        </div>
                    ) : isSuccess ? (
                        <p>{t("move_success", {defaultValue: "Ученики успешно перемещены"})}</p>
                    ) : step === 1 ? (
                        <div>
                            <div style={listStyle}>
                                {schools.map((s) => (
                                    <div
                                        key={s._id || s.id}
                                        style={itemStyle(
                                            selectedSchool?.id === s.id || selectedSchool?._id === s._id,
                                            hoveredId === (s._id || s.id)
                                        )}
                                        onMouseEnter={() => setHoveredId(s._id || s.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                        onClick={() => selectSchool(s)}
                                    >
                                        <div style={{fontWeight: 600}}>{s.name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : step === 2 ? (
                        <div>
                            <div style={{marginBottom: 12, color: "#6b7280"}}>
                                {t("selected_school", {defaultValue: "Выбрана школа"})}: <strong>{selectedSchool?.name}</strong>
                            </div>

                            {classes.length === 0 ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 16
                                        }}
                                    >
                                        <p>
                                            {t("classes_not_found", {defaultValue: " Классов для данной школы еще не найдено"})}
                                        </p>
                                        <Link
                                            to={`/schools/${selectedSchool._id}/classes/create`}
                                        >{t("navigate_to_create_class_from_school", {defaultValue: "Перейти к созданию классов для школы"})}{selectedSchool?.name ? ": " + selectedSchool?.name : ""}</Link>
                                    </div>
                                ) :
                                <div style={listStyle}>
                                    {classes.map((c) => {
                                        const classId = c._id || c.id;
                                        const selectedId = selectedClass?._id || selectedClass?.id;
                                        if (c._id === id) return;
                                        
                                        // Проверка вместимости класса
                                        const currentPupilsCount = c.pupils ? c.pupils.length : 0;
                                        const selectedPupilsCount = selectedPupilIds.length;
                                        const maxCapacity = c.max || 0;
                                        const wouldExceedCapacity = currentPupilsCount + selectedPupilsCount > maxCapacity;
                                        
                                        return (

                                            <div
                                                key={classId}
                                                style={{
                                                    ...itemStyle(selectedId === classId, hoveredId === classId && !wouldExceedCapacity),
                                                    cursor: wouldExceedCapacity ? "not-allowed" : "pointer",
                                                    position: "relative",
                                                    background: wouldExceedCapacity ? "#fee2e2" : (selectedId === classId ? "#f0f4ff04" : hoveredId === classId ? "#f9fbff04" : "#fff"),
                                                    border: wouldExceedCapacity ? "1px solid #fecaca" : (selectedId === classId || hoveredId === classId ? "1px solid #4c6fff" : "1px solid #e5e7eb")
                                                }}
                                                onMouseEnter={() => setHoveredId(classId)}
                                                onMouseLeave={() => setHoveredId(null)}
                                                onClick={wouldExceedCapacity ? undefined : () => selectClassHandler(c)}
                                                title={wouldExceedCapacity ? 
                                                    `Вы хотите переместить ${selectedPupilsCount} учеников, в классе уже есть ${currentPupilsCount} учеников. Максимальное количество учеников ${maxCapacity}` : 
                                                    undefined
                                                }
                                            >
                                                <div style={{fontWeight: 600}}>
                                                    {c.number}{c.letter ? ` ${c.letter}` : ""}
                                                </div>
                                                {c.name && <div style={{color: "#6b7280", fontSize: 14}}>{c.name}</div>}
                                                <div style={{color: "#6b7280", fontSize: 12, marginTop: 4}}>
                                                    {currentPupilsCount}/{maxCapacity} учеников
                                                </div>
                                                {wouldExceedCapacity && hoveredId === classId && (
                                                    <div style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: "rgba(254, 226, 226, 0.95)",
                                                        border: "2px solid #dc2626",
                                                        borderRadius: 10,
                                                        padding: 12,
                                                        fontSize: 12,
                                                        color: "#dc2626",
                                                        zIndex: 10,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        textAlign: "center",
                                                        boxShadow: "0 8px 25px rgba(220, 38, 38, 0.25)",
                                                        backdropFilter: "blur(2px)"
                                                    }}>
                                                        <div>
                                                            <p style={{fontWeight: 600}}>Превышен лимит!</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                        );
                                    })}
                                </div>
                            }

                        </div>
                    ) : (
                        <div>
                            <div style={{marginBottom: 12, color: "#6b7280"}}>
                                {t("selected_school", {defaultValue: "Выбрана школа"})}: <strong>{selectedSchool?.name}</strong>
                            </div>
                            <div style={{marginBottom: 12, color: "#6b7280"}}>
                                {t("selected_class", {defaultValue: "Выбранный класс"})}: <strong>{selectedClass?.number}{selectedClass?.letter ? ` ${selectedClass.letter}` : ""}</strong>
                            </div>
                            <div style={{marginBottom: 12}}>
                                {t("confirm_question", {defaultValue: "Вы действительно хотите переместить учеников"})}:
                                <ul style={{marginTop: 8, paddingLeft: 18}}>
                                    {selectedPupilsFull.map((p) => (
                                        <li key={p._id}>{p.surname} {p.name}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

                <div style={footerStyle}>
                    {step === 3 ? (
                        <>
                            <button
                                className="cancel-btn"
                                style={cancelBtnStyle(hoveredCancel)}
                                onMouseEnter={() => setHoveredCancel(true)}
                                onMouseLeave={() => setHoveredCancel(false)}
                                onClick={handleClose}
                            >
                                {t("cancel", {defaultValue: "Отменить"})}
                            </button>
                            <button
                                className="main-btn"
                                style={confirmBtnStyle(hoveredConfirm, isLoading || isSuccess)}
                                onMouseEnter={() => setHoveredConfirm(true)}
                                onMouseLeave={() => setHoveredConfirm(false)}
                                onClick={handleConfirm}
                                disabled={isLoading || isSuccess}
                            >
                                {t("move", {defaultValue: "Перенести"})}
                            </button>
                        </>
                    ) : (
                        <button
                            className="cancel-btn"
                            style={cancelBtnStyle(hoveredCancel)}
                            onMouseEnter={() => setHoveredCancel(true)}
                            onMouseLeave={() => setHoveredCancel(false)}
                            onClick={handleClose}
                        >
                            {t("cancel", {defaultValue: "Отменить"})}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const PupilsTransfer = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation("pupils");
    const classId = useParams().id;
    const {notice} = useSelector((state) => state.app);
    const [pupils, setPupils] = useState([]);
    const [classData, setClassData] = useState(null);
    const [selectedPupils, setSelectedPupils] = useState([]);
    const [isSureAlert, setIsSureAlert] = useState(false);
    const [isTransferOpen, setIsTransferOpen] = useState(false);

    // После успешного переноса — убрать учеников из списка и снять выделение
    const handleTransferred = (movedIds /*: string[]*/, toClassId /*: string*/) => {
        setPupils((prev) => prev.filter((p) => !movedIds.includes(p._id)));
        setSelectedPupils((prev) => prev.filter((id) => !movedIds.includes(id)));
    };

    useEffect(() => {
        if (classId) {
            (async () => {
                const {data} = await axios.get(`classes/pupils/${classId}`);

                setPupils(data.pupils);
                setClassData(data.class);
            })();
        }
    }, [classId]);

    const hideNoticeHandler = useCallback(() => {
        dispatch(hideNotice());
    }, [dispatch]);

    const showSureAlertHandler = () => {
        setIsSureAlert(true);
    };

    const closeSureAlertHandler = () => {
        setIsSureAlert(false);
    };

    const moveToArchiveHandler = async () => {
        setIsSureAlert(false);

        if (selectedPupils.length) {
            try {
                const {data} = await axios.put(`pupil/archive`, {
                    pupils: selectedPupils,
                    classId,
                });

                setPupils(data.pupils);
            } catch (err) {
                const message = err.response?.data?.message || t("something_went_wrong");
                dispatch(showNotice(message));
            }
        }
    };

    const selectHandler = (e) => {
        const id = e.target.id;
        let updatedSelectedPupils = [];

        if (id === "select-all") {
            if (selectedPupils.length !== pupils.length) {
                updatedSelectedPupils = pupils.map(({_id}) => _id);
            }
        } else {
            updatedSelectedPupils = selectedPupils.includes(id)
                ? selectedPupils.filter((item) => item !== id)
                : [...selectedPupils, id];
        }

        setSelectedPupils(updatedSelectedPupils);
    };

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton/>
                    <p className="page__title">
                        {classData?.number}
                        {classData?.letter}
                    </p>
                </header>

                {!!notice && <Notice text={notice.text} closeHandler={hideNoticeHandler}/>}
                {isSureAlert && (
                    <SureAlert
                        type="moveToArchive"
                        submitText={t("move")}
                        handleClose={closeSureAlertHandler}
                        handleSubmit={moveToArchiveHandler}
                    />
                )}

                <div className="school-page__wrapper">
                    <p className="school-page__text">{t("to_archive_text")}</p>

                    <div className="list" style={{marginTop: 16, padding: "0 16px"}}>
                        <div className="list__checkbox">
                            <input
                                id="select-all"
                                type="checkbox"
                                name="select-all"
                                checked={pupils.length === selectedPupils.length}
                                onChange={selectHandler}
                            />
                            <label htmlFor="select-all">
                                <div>
                                    <p>{t("select_all")}</p>
                                </div>
                            </label>
                        </div>

                        {pupils
                            .filter((p) => !p.isArchive)
                            .map(({_id, surname, name}) => (
                                <div key={_id} className="list__checkbox">
                                    <input
                                        id={_id}
                                        type="checkbox"
                                        name={_id}
                                        checked={selectedPupils.includes(_id)}
                                        onChange={selectHandler}
                                        value={_id}
                                    />
                                    <label htmlFor={_id}>
                                        <div>
                                            <p>
                                                {surname} {name}
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            ))}
                    </div>
                    <div className="send-btns-wrapper">
                        <div className="send-btn" style={{position: "static"}}>
                            <button
                                onClick={showSureAlertHandler}
                                className="main-btn"
                                disabled={!selectedPupils.length}
                            >
                                {t("move_to_archive")}
                            </button>
                        </div>
                        <div className="send-btn" style={{position: "static"}}>
                            <button
                                onClick={() => setIsTransferOpen(true)}

                                className="main-btn"
                                disabled={!selectedPupils.length}
                            >
                                {t("move_pupils")}
                            </button>
                        </div>
                    </div>
                    <TransferModal
                        isOpen={isTransferOpen}
                        onClose={() => setIsTransferOpen(false)}
                        selectedPupilIds={selectedPupils}
                        pupils={pupils}
                        onTransferred={handleTransferred}
                    />
                </div>
            </div>
        </Layout>
    );
};
